import React, { useState } from 'react';
import { useCookieConsent, type CookiePreferences } from '../context/CookieConsentContext';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Cookie, Shield, BarChart3, Target, Wrench } from 'lucide-react';

function CookieCategorySwitch({
  checked,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onCheckedChange(!checked);
      }}
      className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full border-2 p-0.5 transition-colors touch-manipulation ${
        checked ? 'border-green-700 bg-green-600' : 'border-gray-500 bg-gray-300'
      }`}
    >
      <span
        aria-hidden
        className={`block h-6 w-6 rounded-full border border-gray-200 bg-white shadow-md transition-transform duration-200 ease-out ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

const cookieCategories = {
  essential: {
    icon: Shield,
    title: 'Essential',
    required: true,
  },
  functional: {
    icon: Wrench,
    title: 'Functional',
    required: false,
  },
  analytics: {
    icon: BarChart3,
    title: 'Analytics',
    required: false,
  },
  marketing: {
    icon: Target,
    title: 'Marketing',
    required: false,
  },
};

const CookieConsentBanner: React.FC = () => {
  const { state, acceptAll, rejectAll, acceptSelected } = useCookieConsent();
  const [customPreferences, setCustomPreferences] = useState<CookiePreferences>(state.preferences);

  if (!state.showBanner) {
    return null;
  }

  const handleCustomizePreferences = (category: keyof CookiePreferences, enabled: boolean) => {
    if (category === 'essential') return;
    setCustomPreferences((prev) => ({
      ...prev,
      [category]: enabled,
    }));
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-100">
      <div className="px-3 py-2.5 sm:px-4">
        <div className="flex items-center gap-2 mb-2">
          <Cookie className="w-4 h-4 text-blue-600 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900">Cookie Preferences</h3>
          <span className="text-[11px] text-gray-500 truncate">Essential cookies always active</span>
        </div>

        <div className="space-y-1">
          {Object.entries(cookieCategories).map(([key, category]) => {
            const prefKey = key as keyof CookiePreferences;
            const isOn = category.required || customPreferences[prefKey];
            const toggleCategory = () => {
              if (!category.required) {
                handleCustomizePreferences(prefKey, !customPreferences[prefKey]);
              }
            };

            if (category.required) {
              return (
                <div
                  key={key}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 min-h-9 text-xs text-gray-600"
                >
                  <category.icon className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span className="font-medium text-gray-800">{category.title}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                    Required
                  </Badge>
                </div>
              );
            }

            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={toggleCategory}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCategory();
                  }
                }}
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 min-h-10 cursor-pointer touch-manipulation transition-colors ${
                  isOn
                    ? 'border-green-500 bg-green-50/90'
                    : 'border-gray-300 bg-white'
                }`}
              >
                <category.icon className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span id={`cookie-${key}-title`} className="text-sm font-medium text-gray-900 flex-1 min-w-0">
                  {category.title}
                </span>
                <Badge
                  className={`text-[10px] font-bold px-1.5 py-0 h-5 shrink-0 ${
                    isOn
                      ? 'bg-green-600 text-white hover:bg-green-600'
                      : 'bg-gray-500 text-white hover:bg-gray-500'
                  }`}
                >
                  {isOn ? 'Allowed' : 'Blocked'}
                </Badge>
                <CookieCategorySwitch
                  checked={isOn}
                  onCheckedChange={(checked) => handleCustomizePreferences(prefKey, checked)}
                  label={`${category.title}: ${isOn ? 'allowed' : 'blocked'}`}
                />
              </div>
            );
          })}
        </div>

        <div className="mt-2.5 grid grid-cols-3 gap-1.5 sm:gap-2">
          <Button size="sm" onClick={acceptAll} className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm">
            Accept All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => acceptSelected(customPreferences)}
            className="text-xs sm:text-sm"
          >
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={rejectAll} className="text-xs sm:text-sm">
            Reject All
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
